/**
 * Générateur — Il associe à des objets des ordres de grandeur en lien avec
 * d'autres disciplines.
 * Attendu ID : 145 | Niveau : 4ème | Domaine : Nombres (desc 11, O10)
 *
 * Source doc prof : Créé n°6 (relier objet ↔ ordre de grandeur) + image p.12
 * (problèmes : atome, bactérie, alvéole, distance Terre-Lune, piscine olympique).
 * Plan de cartes validé 2026-07-14, resserré le 2026-09-22 :
 *
 *   Carte « relier » — objet ↔ ordre de grandeur a × 10ⁿ, trois objets d'une
 *                      même unité contre cinq ordres de grandeur, dont deux
 *                      leurres (relier)
 *   Carte « ordre » — ordre de grandeur d'un objet donné : même objet, même
 *                     coefficient, même unité, seule la puissance de 10 change
 *                     (qcm_unique)
 *
 * ⚠️ DÉCISION SIGNALÉE : les deux cartes reposent sur une TABLE DE RÉFÉRENCE
 * physique (ordres de grandeur standard) — part non-mathématique assumée par le
 * doc, comme les préfixes SI. Chaque objet ajouté porte sa valeur de référence
 * et sa provenance en commentaire.
 *
 * Revue 2026-09-22 (audit dénombré, décisions prof) :
 *   - Carte « choisir » SUPPRIMÉE (miroir exact de « ordre » sur la même
 *     table, même fuite, pas réparable de la même façon).
 *   - Carte « rapport » SUPPRIMÉE (quotient de deux puissances de dix sans
 *     objet nommé : c'est l'attendu 136, pas l'association à un objet).
 *   - « ordre » : 54 % des tirages avaient un distracteur mort (autre unité) ;
 *     les distracteurs sont maintenant le même objet avec l'exposant décalé de
 *     un, deux ou trois rangs, la question devient « quelle puissance de dix ».
 *   - « relier » : 64 % des tirages contenaient la seule paire en kg ou la
 *     seule paire sans unité, appariable par l'unité seule ; les cinq objets
 *     sont tirés dans une même unité, exposants distincts dans le tirage.
 *   - Donnée fausse corrigée : la taille d'un virus valait 10⁻⁸ m ; les virus
 *     mesurent de 20 à 300 nm, les plus courants (grippe, coronavirus) autour
 *     de 100 nm, soit 10⁻⁷ m.
 *   - Table étoffée (13 → 41 objets, dont 13 masses en kg) : la contrainte
 *     globale « exposants tous distincts » est remplacée par une contrainte au
 *     tirage (exposants distincts dans un même relier seulement).
 *
 * Revue 2026-09-23 (typographie, règle prof) : tout ordre de grandeur AFFICHÉ
 * (cibles du relier, options du QCM) passe en KaTeX via tex / puissTex, plus
 * d'exposant Unicode (helper `puiss` de nombres.js abandonné ici). L'unité
 * (« m », « kg ») reste du texte à côté de la formule : c'est un symbole, pas
 * une expression mathématique.
 *
 * Règle prof 2026-09-23 (composition des relier) : toujours moins d'éléments
 * à gauche qu'à droite, trois au maximum à gauche. La carte « relier » passe
 * de cinq paires à TROIS objets contre CINQ ordres de grandeur : les trois
 * bons et deux cibles leurres (cibles_leurres de makeRelier). Un leurre est
 * l'ordre de grandeur d'un des objets tirés avec l'exposant décalé d'un ou
 * deux rangs, même coefficient, même unité : la confusion réelle de l'attendu
 * est la puissance de dix, pas la grandeur, et un ordre de grandeur d'une
 * autre unité serait éliminé sans réfléchir (le distracteur mort mesuré à la
 * revue du 2026-09-22). Les cinq exposants affichés sont distincts par
 * construction et la chaîne affichée de chaque leurre est comparée à celles
 * déjà prises : aucun leurre ne vaut une bonne cible ni l'autre leurre. Le
 * code du leurre nomme l'erreur : objet surestimé ou sous-estimé.
 */

import {
    randomChoice, shuffle, tex, puissTex,
    makeQcm, makeRelier,
} from '../../../utils';

const ATTENDU_ID = 145;

const L_RELIER = 'Associer un objet à son ordre de grandeur';
const L_ORDRE = 'Choisir l\'ordre de grandeur d\'un objet';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Table de référence. Un objet = { obj, coef (chaîne FR ou null), e, unit }.
// Les exposants peuvent se répéter d'un objet à l'autre : c'est le TIRAGE de la
// carte « relier » qui garantit des exposants distincts entre ses trois objets
// et ses deux leurres.
// Pour chaque objet ajouté le 2026-09-22 : valeur de référence et provenance.
const BANQUE = [
    // ── longueurs (m) ──
    { obj: 'la taille d\'un atome', coef: null, e: -10, unit: 'm' },
    // Virus : 20 à 300 nm, les plus courants (grippe, coronavirus) autour de
    // 100 nm = 10⁻⁷ m (valait 10⁻⁸ m, corrigé le 2026-09-22).
    { obj: 'la taille d\'un virus', coef: null, e: -7, unit: 'm' },
    { obj: 'la taille d\'une bactérie', coef: null, e: -6, unit: 'm' },
    { obj: 'l\'épaisseur d\'un cheveu', coef: null, e: -4, unit: 'm' },
    { obj: 'la longueur d\'une fourmi', coef: null, e: -3, unit: 'm' },
    { obj: 'la longueur d\'une piscine olympique', coef: '5', e: 1, unit: 'm' },
    { obj: 'la hauteur de la tour Eiffel', coef: '3', e: 2, unit: 'm' },
    { obj: 'l\'altitude de l\'Everest', coef: '9', e: 3, unit: 'm' },
    { obj: 'le rayon de la Terre', coef: '6', e: 6, unit: 'm' },
    { obj: 'la distance Terre-Lune', coef: '4', e: 8, unit: 'm' },
    { obj: 'la distance Terre-Soleil', coef: '1,5', e: 11, unit: 'm' },
    // Ajouts 2026-09-22 :
    { obj: 'la taille d\'un globule rouge', coef: '7', e: -6, unit: 'm' },           // 7 à 8 µm (SVT, Wikipédia « Érythrocyte »)
    { obj: 'l\'épaisseur d\'une feuille de papier', coef: null, e: -4, unit: 'm' },   // ≈ 0,1 mm pour du papier de 80 g/m²
    { obj: 'l\'épaisseur d\'une pièce de 1 euro', coef: '2', e: -3, unit: 'm' },      // 2,33 mm (Banque centrale européenne)
    { obj: 'la longueur d\'un stylo', coef: '1,5', e: -1, unit: 'm' },               // ≈ 15 cm (stylo à bille courant : 14,9 cm)
    { obj: 'la hauteur d\'une porte', coef: '2', e: 0, unit: 'm' },                  // 2,04 m (porte intérieure standard)
    { obj: 'la longueur d\'un bus', coef: '1,2', e: 1, unit: 'm' },                  // 12 m (autobus standard)
    { obj: 'la longueur d\'un terrain de football', coef: null, e: 2, unit: 'm' },   // 105 m (règles FIFA)
    { obj: 'la profondeur de la fosse des Mariannes', coef: null, e: 4, unit: 'm' }, // 10 994 m (Challenger Deep)
    { obj: 'le tour de la Terre à l\'équateur', coef: '4', e: 7, unit: 'm' },        // 40 075 km
    { obj: 'le diamètre de Jupiter', coef: '1,4', e: 8, unit: 'm' },                 // 139 820 km (NASA)
    { obj: 'le diamètre du Soleil', coef: '1,4', e: 9, unit: 'm' },                  // 1 392 700 km (NASA)
    { obj: 'la distance de la Terre à l\'étoile la plus proche après le Soleil', coef: '4', e: 16, unit: 'm' }, // Proxima du Centaure, 4,24 années-lumière ≈ 4,0 × 10¹⁶ m
    { obj: 'le diamètre de la Voie lactée', coef: null, e: 21, unit: 'm' },          // ≈ 100 000 années-lumière ≈ 9,5 × 10²⁰ m
    // ── masses (kg) ──
    { obj: 'la masse de la Terre', coef: '6', e: 24, unit: 'kg' },
    // Ajouts 2026-09-22 :
    { obj: 'la masse d\'un électron', coef: '9', e: -31, unit: 'kg' },               // 9,109 × 10⁻³¹ kg (CODATA 2018)
    { obj: 'la masse d\'un atome d\'hydrogène', coef: '1,7', e: -27, unit: 'kg' },   // 1,674 × 10⁻²⁷ kg (CODATA 2018)
    { obj: 'la masse d\'un moustique', coef: '2,5', e: -6, unit: 'kg' },             // 2 à 2,5 mg (Wikipédia « Moustique »)
    { obj: 'la masse d\'un grain de riz', coef: '2,5', e: -5, unit: 'kg' },          // 20 à 30 mg (poids de mille grains ≈ 25 g)
    { obj: 'la masse d\'un trombone', coef: null, e: -3, unit: 'kg' },               // ≈ 1 g (trombone de bureau standard)
    { obj: 'la masse d\'une pièce de 1 euro', coef: '7,5', e: -3, unit: 'kg' },      // 7,50 g (Banque centrale européenne)
    { obj: 'la masse d\'une pomme', coef: '1,5', e: -1, unit: 'kg' },                // 150 à 200 g (fruit de calibre courant)
    { obj: 'la masse d\'un adulte', coef: '7', e: 1, unit: 'kg' },                   // ≈ 70 kg
    { obj: 'la masse d\'une voiture', coef: null, e: 3, unit: 'kg' },                // 1 000 à 1 500 kg (voiture particulière)
    { obj: 'la masse d\'un éléphant d\'Afrique', coef: '5', e: 3, unit: 'kg' },      // 4 à 7 t pour un mâle adulte (Wikipédia « Éléphant de savane d'Afrique »)
    { obj: 'la masse d\'une baleine bleue', coef: null, e: 5, unit: 'kg' },          // 100 à 150 t (Wikipédia « Baleine bleue »)
    { obj: 'la masse de la Lune', coef: '7', e: 22, unit: 'kg' },                    // 7,35 × 10²² kg (NASA)
    { obj: 'la masse du Soleil', coef: '2', e: 30, unit: 'kg' },                     // 1,989 × 10³⁰ kg (NASA)
    // ── nombres sans unité ──
    { obj: 'le nombre d\'habitants sur Terre', coef: '8', e: 9, unit: '' },
    // Ajouts 2026-09-22 :
    { obj: 'le nombre d\'habitants en France', coef: '7', e: 7, unit: '' },          // 68,4 millions au 1er janvier 2024 (Insee)
    { obj: 'le nombre de neurones du cerveau humain', coef: '9', e: 10, unit: '' },  // ≈ 86 milliards (Herculano-Houzel, 2009)
];

// Ordre de grandeur affiché : « $1{,}5 \times 10^{11}$ m ». La virgule décimale
// s'écrit {,} en KaTeX (sinon elle est espacée comme une ponctuation).
const mag = (b) => {
    const formule = b.coef
        ? tex(`${b.coef.replace(',', '{,}')} \\times 10^{${b.e}}`)
        : puissTex(10, b.e);
    return `${formule}${b.unit ? ` ${b.unit}` : ''}`;
};
// Majuscule initiale pour un libellé de tuile.
const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);
// Contraction « de + article » (« de le rayon » → « du rayon », « de la taille »
// → « de la taille », « de l'atome » → « de l'atome »).
const deObj = (obj) => obj.startsWith('le ') ? `du ${obj.slice(3)}`
    : obj.startsWith('les ') ? `des ${obj.slice(4)}`
        : `de ${obj}`;

// Unités qui comptent au moins cinq exposants distincts : seules elles peuvent
// nourrir la carte « relier » (les nombres sans unité restent à la carte « ordre »).
// Trois objets seulement sont tirés depuis le 2026-09-23, mais un vivier de
// trois exposants donnerait toujours le même tirage : le seuil reste à cinq.
const UNITES_RELIER = [...new Set(BANQUE.map(b => b.unit))]
    .filter(u => new Set(BANQUE.filter(b => b.unit === u).map(b => b.e)).size >= 5);

// ═════════ Carte « relier » — objet ↔ ordre de grandeur, une seule unité ═════════

// Décalage d'exposant d'un leurre : un ou deux rangs, vers le haut ou le bas.
const DECALAGES_LEURRE = [-2, -1, 1, 2];
// L'erreur d'élève qu'un leurre incarne : l'objet surestimé (exposant trop
// grand) ou sous-estimé (exposant trop petit) d'un ou deux rangs.
const codeLeurre = (d) => (d > 0 ? 'exposant_trop_grand' : 'exposant_trop_petit');
// Clé de comparaison d'une cible affichée : la chaîne elle-même, sans casse ni
// espaces (au moins aussi sévère que la normalisation du scoring).
const cle = (s) => s.toLowerCase().replace(/\s+/g, '');

const genRelier = () => {
    const unit = randomChoice(UNITES_RELIER);
    // Trois objets de la même unité, exposants distincts DANS CE TIRAGE (deux
    // cibles de même exposant seraient indiscernables). Un seul passage sur la
    // table mélangée suffit : chaque unité admise porte au moins cinq exposants.
    const sel = [];
    const pris = new Set();                     // exposants affichés, leurres compris
    shuffle(BANQUE.filter(b => b.unit === unit)).forEach(b => {
        if (sel.length < 3 && !pris.has(b.e)) { sel.push(b); pris.add(b.e); }
    });
    const pairs = sel.map(b => ({ source: capital(b.obj), target: mag(b) }));
    // Deux cibles leurres (règle prof 2026-09-23) : deux des trois objets,
    // chacun avec son exposant décalé d'un ou deux rangs, même coefficient,
    // même unité. L'exposant d'un leurre n'est celui d'aucune autre cible,
    // bonne ou leurre : la question reste « quelle puissance de dix ». Sur les
    // quatre décalages possibles, trois au plus sont pris (les deux autres
    // objets et le premier leurre) : chaque objet en fournit toujours un. La
    // chaîne affichée est comparée en plus à celles déjà prises.
    const affiches = new Set(pairs.map(p => cle(p.target)));
    const leurres = [];
    shuffle(sel).forEach(b => {
        if (leurres.length >= 2) return;
        const d = shuffle(DECALAGES_LEURRE)
            .find(k => !pris.has(b.e + k) && !affiches.has(cle(mag({ ...b, e: b.e + k }))));
        if (d === undefined) return;
        const target = mag({ ...b, e: b.e + d });
        pris.add(b.e + d);
        affiches.add(cle(target));
        leurres.push({ target, code: codeLeurre(d), e: b.e, d });
    });
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque objet à son ordre de grandeur.</b> Deux ordres de grandeur sont en trop.</p>',
        { ...opts(L_RELIER), cibles_leurres: leurres.map(({ target, code }) => ({ target, code })) });
    payload._meta.params = { unit, objs: sel.map(b => b.e), leurres: leurres.map(({ e, d }) => ({ e, d })) };
    return payload;
};

// ═════════ Carte « ordre » — quelle puissance de dix pour cet objet ? (QCM) ═════════

const genOrdreQcm = () => {
    const b = randomChoice(BANQUE);
    // Même objet, même coefficient, même unité : seule la puissance de dix
    // change, décalée de un, deux ou trois rangs, vers le haut ou vers le bas.
    const decalages = shuffle([-3, -2, -1, 1, 2, 3]).slice(0, 3);
    const choix = [b, ...decalages.map(d => ({ ...b, e: b.e + d }))].map(mag);
    const payload = makeQcm(choix, mag(b),
        `<p><b>Quel est l'ordre de grandeur ${deObj(b.obj)} ?</b></p>`, opts(L_ORDRE));
    payload._meta.params = { e: b.e, decalages };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    relier_relier: genRelier,
    ordre_qcm_unique: genOrdreQcm,
};

const FORMAT_LABELS = { relier: 'Relier', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'relier', label: L_RELIER, formatsList: buildFormats('relier') },
    { id: 'ordre', label: L_ORDRE, formatsList: buildFormats('ordre') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
